import { makeStyles } from "@material-ui/core";
import React, { useContext } from "react";
import { useHistory } from "react-router-dom";
import { formatDate } from "./shared/helpers/helper";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { viewportContext } from "../context/ViewportContext";
import { tokens } from "../theme/tokens";
import { LoadingSkeleton } from "./shared/helpers/StatePrimitives";

const CASE_TYPE_TOKEN = {
  "Surplus Funds": tokens.color.category.caseType.surplusFunds,
  "Locate for Service of Process": tokens.color.category.caseType.locateForService,
  "PI Investigation": tokens.color.category.caseType.piInvestigation,
  "Locate Witness": tokens.color.category.caseType.locateWitness,
  Background: tokens.color.category.caseType.background,
};

const useStyles = makeStyles(() => ({
  card: {
    flex: "1 0 320px",
    minWidth: "320px",
    maxWidth: "480px",
    backgroundColor: tokens.color.surface.default,
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.md,
    boxShadow: "none",
    padding: tokens.space[3],
    marginTop: tokens.space[2],
    display: "flex",
    flexDirection: "column",
    cursor: "pointer",
    transition: `border-color ${tokens.motion.fast} ${tokens.motion.easing}`,
    "&:hover": { borderColor: tokens.color.border.strong },
    "&:focus-visible": {
      outline: `${tokens.focus.width} solid ${tokens.color.focus.ring}`,
      outlineOffset: tokens.focus.offset,
    },
  },
  head: {
    display: "flex",
    alignItems: "center",
    gap: tokens.space[2],
    marginBottom: tokens.space[2],
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.space[1],
    padding: `${tokens.space[1]} ${tokens.space[2]}`,
    backgroundColor: tokens.color.surface.subtle,
    borderRadius: tokens.radius.sm,
    fontSize: tokens.type.caption.size,
    lineHeight: tokens.type.caption.lineHeight,
    color: tokens.color.text.primary,
    whiteSpace: "nowrap",
  },
  dot: {
    width: tokens.space[2],
    height: tokens.space[2],
    borderRadius: tokens.radius.full,
    flex: "0 0 auto",
  },
  client: {
    marginLeft: "auto",
    fontSize: tokens.type.dense.size,
    fontWeight: tokens.type.denseStrong.weight,
    color: tokens.color.text.primary,
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    gap: tokens.space[3],
    padding: `${tokens.space[1]} 0`,
  },
  label: {
    fontSize: tokens.type.caption.size,
    color: tokens.color.text.secondary,
  },
  value: {
    fontSize: tokens.type.dense.size,
    color: tokens.color.text.primary,
    textAlign: "right",
  },
  fileNo: {
    fontSize: tokens.type.dense.size,
    fontWeight: tokens.type.denseStrong.weight,
    color: tokens.color.brand.primaryAction,
    textAlign: "right",
  },
}));

function CaseCards({ e, view, caseLoader }) {
  const history = useHistory();
  const classes = useStyles();
  const { tallScreen } = useContext(viewportContext);

  const returnField = (v) => (v ? v : "-");

  if (caseLoader) {
    return (
      <div className={classes.card} style={tallScreen ? { minWidth: "350px" } : {}}>
        <div className={classes.head}>
          <LoadingSkeleton variant="circular" width={tokens.icon.lg} height={tokens.icon.lg} />
          <LoadingSkeleton variant="text" width="40%" />
          <div style={{ marginLeft: "auto", width: "30%" }}>
            <LoadingSkeleton variant="text" />
          </div>
        </div>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} style={{ padding: `${tokens.space[1]} 0` }}>
            <LoadingSkeleton variant="text" />
          </div>
        ))}
      </div>
    );
  }

  const dotColor = e.case_type ? CASE_TYPE_TOKEN[e.case_type] : undefined;
  const inactive = view === "inactive";

  const rows = [
    ["Status", returnField(e.status)],
    ["File #", e.file_no, "fileNo"],
    ["Created At", formatDate(returnField(e.created_at))],
    ["Client File #", returnField(e.client_file_no)],
    ["Due Date", formatDate(returnField(e.due_date))],
    ["Invoice Date", formatDate(returnField(e.invoice_date))],
    ["Primary Subject", returnField(e.subject_name)],
    ["Follow up", formatDate(returnField(e.follow_up))],
    ["Event", returnField(e.event)],
    ["Servicer", returnField(e.client_loan)],
    ["User", returnField(e.user)],
  ];

  return (
    <div
      className={classes.card}
      style={tallScreen ? { minWidth: "350px" } : {}}
      role="button"
      tabIndex={0}
      onClick={() => history.push("/app/cases/" + e.file_no + "/case")}
      onKeyDown={(ev) => {
        if (ev.key === "Enter" || ev.key === " ")
          history.push("/app/cases/" + e.file_no + "/case");
      }}
    >
      <div className={classes.head}>
        <WorkOutlineIcon
          sx={{
            fontSize: tokens.icon.default,
            color: inactive ? tokens.color.text.disabled : tokens.color.brand.primaryAction,
          }}
        />
        {e.case_type && (
          <span className={classes.chip}>
            {dotColor && (
              <span className={classes.dot} style={{ backgroundColor: dotColor }} />
            )}
            {e.case_type}
          </span>
        )}
        <span className={classes.client}>{returnField(e.client)}</span>
      </div>

      {rows.map(([label, value, kind]) => (
        <div className={classes.row} key={label}>
          <span className={classes.label}>{label}</span>
          <span className={kind === "fileNo" ? classes.fileNo : classes.value}>
            {value}
          </span>
        </div>
      ))}
    </div>
  );
}

export default CaseCards;
